import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useLatestLoad } from './useLatestLoad'
import { useUnsavedWork } from '../contexts/UnsavedWorkContext'
import { ApiError } from '../core/api/http'
import { errorMessage } from '../core/errors'
import type { RequestOptions } from '../core/types/backend'

type Options<TData, TDraft> = {
  /** GET; stabiel (module-functie of useCallback). */
  load: (opts: RequestOptions) => Promise<TData>
  /** Het bewerkbare deel van het antwoord (bijv. zonder door de server getelde aantallen). Stabiel. */
  toDraft: (data: TData) => TDraft
  /** PUT met de versie uit `data`; geeft de nieuwe stand terug. */
  persist: (draft: TDraft, data: TData) => Promise<TData>
  /** Toasts: wat er goed ging, en waar een 409 over gaat ("De doelgroepen"). */
  savedTitle: string
  savedText?: string
  conflictSubject: string
}

/**
 * Eén blok van de server lokaal bewerken en in één keer opslaan: laden, concept, "gewijzigd",
 * opslaan met versiecontrole (409 `version_conflict` → verversen aanbieden) en verwerpen.
 * Nooit optimistisch. Zolang er iets niet is opgeslagen, waarschuwt het register
 * (contexts/UnsavedWorkContext) bij het sluiten van het tabblad en bij doorklikken in het beheer.
 * Gedeeld door de mails, de doelgroepen en de doorverwijzingen.
 */
export function useServerDraft<TData, TDraft>({ load, toDraft, persist, savedTitle, savedText, conflictSubject }: Options<TData, TDraft>) {
  const { data, setData, status, reload } = useLatestLoad(load)
  const [draft, setDraft] = useState<TDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)

  const base = useMemo(() => (data ? toDraft(data) : null), [data, toDraft])
  const current = draft ?? base
  const dirty = useMemo(() => Boolean(draft && base && JSON.stringify(draft) !== JSON.stringify(base)), [draft, base])

  const edit = useCallback(
    (fn: (d: TDraft) => TDraft) =>
      setDraft((d) => {
        const from = d ?? base
        return from === null ? d : fn(from)
      }),
    [base],
  )
  const discard = useCallback(() => setDraft(null), [])

  const save = useCallback(async () => {
    if (!draft || !data || savingRef.current) return false
    savingRef.current = true
    setSaving(true)
    try {
      setData(await persist(draft, data))
      setDraft(null)
      toast.success(savedTitle, savedText ? { description: savedText } : undefined)
      return true
    } catch (err) {
      if (err instanceof ApiError && err.code === 'version_conflict') {
        toast.error(`${conflictSubject} zijn intussen ergens anders opgeslagen`, {
          description: 'Ververs om die versie te zien. Je wijzigingen van hier gaan dan verloren.',
          duration: Infinity,
          action: { label: 'Verversen', onClick: () => window.location.reload() },
        })
      } else {
        toast.error('Opslaan lukte niet', { description: errorMessage(err) ?? 'Probeer het zo nog eens. Je wijzigingen staan nog klaar.' })
      }
      return false
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }, [draft, data, persist, setData, savedTitle, savedText, conflictSubject])

  // Tabblad sluiten én doorklikken in het beheer: het gedeelde register vraagt eerst "Weggaan?".
  useUnsavedWork(dirty, 'page')

  return { data, current, status, reload, dirty, saving, edit, save, discard }
}
